import { type BundleStore } from "../store-contract";
import {
  type BundleFacility,
  type BundleTarget,
  type BundleUtilityReadiness,
  type BundleWorkAssignment,
  type ChangeRequestCategoryTemplate,
  type ChangeRequestPendingNewInstallations,
  type ChangeRequestSet,
  deriveKitOwnerLabel,
  type FieldFacilityDeviceRow,
  type FieldworkBundle,
  type StoredAssignment,
  type StoredFacility,
  type StoredHazardType,
  type StoredRequirementVerdict,
  type StoredSupplement,
  type StoredUtilityRequirements,
  type WorkAssignmentStatus,
} from "../types";
import {
  compareStrings,
  deleteRowsWhere,
  getAllRows,
  getRow,
  requestToPromise,
  txDone,
} from "./db";

// The bundle-snapshot group over the per-profile database, transcribed from
// the device store's SQL: wholesale-replaced on every pull, zero merge logic
// anywhere. Field scope wipes everything (the field bundle is cross-org); org
// scope replaces one org's rows and prunes version/target rows no longer
// referenced by any remaining assignment, and leaves the three field-only
// stores (category templates, change-request sets, catalog versions) alone.

function targetKey(facilityId: string, deviceInstallationId: string | null | undefined): string {
  return `${facilityId}:${deviceInstallationId ?? ""}`;
}

// Synthetic single-column PK for bundle_supplements: the per-(company,
// version) dedup key, mirroring the targetKey precedent. The wire already
// dedupes supplements to the highest published version per (company,
// template), so this composite is unique by construction.
function supplementKey(companyId: string, templateVersionId: string): string {
  return `${companyId}:${templateVersionId}`;
}

interface AssignmentRow {
  id: string;
  org_id: string;
  org_name: string;
  facility_id: string;
  device_installation_id: string | null;
  template_id: string;
  template_version_id: string;
  target_kind: string;
  status: string;
  local_status: WorkAssignmentStatus | null;
  due_date: string;
  origin: string;
  payload_json: string;
}

interface TemplateVersionRow {
  id: string;
  template_id: string;
  version: number;
  title: string;
  target_kind: string;
  device_category: string | null;
  review_required: boolean;
  definition_json: string;
}

interface TargetRow {
  target_key: string;
  facility_id: string;
  device_installation_id: string | null;
  payload_json: string;
}

interface KitRow {
  id: string;
  owner_kind: string;
  owner_label: string;
  manufacturer: string;
  model: string | null;
  serial_number: string;
  status: string;
  calibrated_at: string | null;
  calibration_expires_at: string | null;
}

interface SupplementRow {
  supplement_key: string;
  company_id: string;
  company_name: string;
  device_category: string | null;
  template_id: string;
  template_version_id: string;
  title: string;
  definition_json: string;
}

interface FacilityRow {
  facility_id: string;
  org_id: string;
  org_name: string;
  name: string;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state_code: string | null;
  zip_code: string | null;
  county: string | null;
  pws_id: string | null;
  facility_type: string | null;
  compliance_status: string | null;
  context_json: string;
}

interface WalkupTemplateRow {
  template_id: string;
  org_id: string;
  template_version_id: string;
  title: string;
  target_kind: string;
}

// One org's readiness block, keyed by the org it belongs to. The verdict list
// is serialized once at store time — the verbatim-storage precedent the
// definition and context blobs set — so the row stays one column wider than
// the block's own fields whatever the vocabulary grows into.
interface RequirementsRow {
  org_id: string;
  org_name: string;
  grant_status: string;
  blocked: boolean;
  requirements_json: string;
  // null when the block carried none.
  change_request_evidence_json: string | null;
  timezone: string | null;
  // From the bundle's utility block naming this org — its correction window
  // and its flattened active hazard types, serialized once like the verdicts;
  // both null when no utility block names the org.
  correction_window_days: number | null;
  hazard_types_json: string | null;
}

// One utility's violation vocabulary as the field bundle carries it.
type BundleUtility = NonNullable<FieldworkBundle["utilities"]>[number];

// Keyed `org_id:device_category` — one template per org and category.
interface CategoryTemplateRow {
  template_key: string;
  payload_json: string;
}

// The wire set verbatim, with its position in the bundle so a read returns
// the server's order.
interface ChangeRequestSetBundleRow {
  id: string;
  position: number;
  payload_json: string;
}

interface CatalogVersionRow {
  org_id: string;
  token: string;
}

function categoryTemplateKey(orgId: string, deviceCategory: string): string {
  return `${orgId}:${deviceCategory}`;
}

interface SyncMetaRow {
  key: string;
  value: string;
}

// The two columns the pending-preserve prune reads off the walk-up create
// queue; the full row shape belongs to the outbox store.
interface PendingAssignmentRow {
  assignment_id: string;
  state: string;
}

// The decoded shape of context_json: the wire read_context map plus the
// resolved collections + truncation flag, stored together verbatim.
interface ContextBlob {
  read_context: Record<string, string>;
  collections?: Record<string, Array<Record<string, string>>>;
  collections_truncated?: boolean;
  devices?: FieldFacilityDeviceRow[];
  pending_new_installations?: ChangeRequestPendingNewInstallations;
}

// Store names the bundle snapshot writes, plus the walk-up create queue the
// pending-preserve prune reads.
const REPLACE_STORES = [
  "bundle_assignments",
  "bundle_template_versions",
  "bundle_targets",
  "bundle_kits",
  "bundle_supplements",
  "bundle_facilities",
  "bundle_walkup_templates",
  "bundle_requirements",
  "bundle_category_templates",
  "bundle_change_request_sets",
  "bundle_catalog_versions",
  "sync_meta",
  "outbox_assignments",
] as const;

const PENDING_WALKUP_STATES = new Set(["queued", "sending", "error"]);

async function pendingWalkupAssignmentIds(tx: IDBTransaction): Promise<Set<string>> {
  const rows = await getAllRows<PendingAssignmentRow>(tx.objectStore("outbox_assignments"));
  return new Set(
    rows.filter((r) => PENDING_WALKUP_STATES.has(r.state)).map((r) => r.assignment_id),
  );
}

// SQLite LIKE folds ASCII and nothing beyond, so the fold is A-Z-only.
function asciiLowerCase(s: string): string {
  return s.replace(/[A-Z]/g, (ch) => ch.toLowerCase());
}

function assignmentRow(a: BundleWorkAssignment): AssignmentRow {
  return {
    id: a.id,
    org_id: a.org_id,
    org_name: a.org_name ?? "",
    facility_id: a.facility_id,
    device_installation_id: a.device_installation_id ?? null,
    template_id: a.template_id,
    template_version_id: a.template_version_id,
    target_kind: a.target_kind,
    status: a.status,
    local_status: null,
    due_date: a.due_date,
    origin: a.origin,
    payload_json: JSON.stringify(a),
  };
}

function facilityRow(f: BundleFacility): FacilityRow {
  return {
    facility_id: f.facility_id,
    org_id: f.org_id,
    org_name: f.org_name,
    name: f.name,
    address_line1: f.address_line1 ?? null,
    address_line2: f.address_line2 ?? null,
    city: f.city ?? null,
    state_code: f.state_code ?? null,
    zip_code: f.zip_code ?? null,
    county: f.county ?? null,
    pws_id: f.pws_id ?? null,
    facility_type: f.facility_type ?? null,
    compliance_status: f.compliance_status ?? null,
    // The wire read_context + collections/collections_truncated stored
    // verbatim together as ONE JSON blob — the definition_json
    // verbatim-storage precedent, widened rather than adding a column.
    context_json: JSON.stringify({
      read_context: f.read_context,
      collections: f.collections,
      collections_truncated: f.collections_truncated,
      devices: f.devices,
      pending_new_installations: f.pending_new_installations,
    } satisfies ContextBlob),
  };
}

function toStoredAssignment(row: AssignmentRow): StoredAssignment {
  return {
    // JSON.parse returns unknown-shaped data; payload_json was written as
    // JSON.stringify of a wire WorkAssignment, so the cast restores that.
    assignment: JSON.parse(row.payload_json) as BundleWorkAssignment,
    localStatus: row.local_status,
  };
}

function toStoredSupplement(r: SupplementRow): StoredSupplement {
  return {
    companyId: r.company_id,
    companyName: r.company_name,
    deviceCategory: r.device_category,
    templateId: r.template_id,
    templateVersionId: r.template_version_id,
    title: r.title,
    // definition_json was written as JSON.stringify of a wire definition
    // document (an object), so the cast restores that shape.
    definition: JSON.parse(r.definition_json) as Record<string, unknown>,
  };
}

// `utility` is the bundle's utility block for the SAME org, looked up by org id
// — the two lists sit behind different fences, so their positions never align.
function requirementsRow(
  u: BundleUtilityReadiness,
  utility: BundleUtility | undefined,
): RequirementsRow {
  return {
    org_id: u.org_id,
    org_name: u.org_name,
    grant_status: u.grant_status,
    blocked: u.blocked,
    requirements_json: JSON.stringify(
      u.requirements.map(
        (v): StoredRequirementVerdict => ({
          id: v.id,
          level: v.level,
          status: v.status,
          cure: v.cure,
          validUntil: v.valid_until,
        }),
      ),
    ),
    change_request_evidence_json:
      u.change_request_evidence === undefined
        ? null
        : JSON.stringify({
            review: u.change_request_evidence.review,
            block: u.change_request_evidence.block,
            reviewWindowDays: u.change_request_evidence.review_window_days,
          } satisfies StoredUtilityRequirements["changeRequestEvidence"]),
    timezone: u.timezone ?? null,
    correction_window_days: utility?.correction_window_days ?? null,
    hazard_types_json:
      utility === undefined
        ? null
        : JSON.stringify(
            utility.hazard_types.map(
              (h): StoredHazardType => ({ id: h.id, label: h.label, sortOrder: h.sort_order }),
            ),
          ),
  };
}

function toStoredUtilityRequirements(r: RequirementsRow): StoredUtilityRequirements {
  return {
    orgId: r.org_id,
    orgName: r.org_name,
    grantStatus: r.grant_status,
    blocked: r.blocked,
    // requirements_json was written as JSON.stringify of the flattened
    // verdict list.
    requirements: JSON.parse(r.requirements_json) as StoredRequirementVerdict[],
    changeRequestEvidence:
      r.change_request_evidence_json === null
        ? undefined
        : // Written as JSON.stringify of the flattened evidence levels.
          (JSON.parse(
            r.change_request_evidence_json,
          ) as StoredUtilityRequirements["changeRequestEvidence"]),
    timezone: r.timezone ?? undefined,
    ...(r.correction_window_days === null
      ? {}
      : { correctionWindowDays: r.correction_window_days }),
    // Written as JSON.stringify of the flattened hazard-type list.
    ...(r.hazard_types_json === null
      ? {}
      : { hazardTypes: JSON.parse(r.hazard_types_json) as StoredHazardType[] }),
  };
}

function toStoredFacility(r: FacilityRow): StoredFacility {
  // context_json was written as JSON.stringify of a ContextBlob.
  const blob = JSON.parse(r.context_json) as ContextBlob;
  return {
    facilityId: r.facility_id,
    orgId: r.org_id,
    orgName: r.org_name,
    name: r.name,
    addressLine1: r.address_line1,
    addressLine2: r.address_line2,
    city: r.city,
    stateCode: r.state_code,
    zipCode: r.zip_code,
    county: r.county,
    pwsId: r.pws_id,
    facilityType: r.facility_type,
    complianceStatus: r.compliance_status,
    readContext: blob.read_context,
    collections: blob.collections,
    collectionsTruncated: blob.collections_truncated,
    devices: blob.devices,
    pendingNewInstallations: blob.pending_new_installations,
  };
}

export function createBundleStore(db: IDBDatabase): BundleStore {
  const readStore = (name: string): IDBObjectStore =>
    db.transaction(name, "readonly").objectStore(name);

  return {
    async replaceBundle(scope, bundle) {
      const tx = db.transaction([...REPLACE_STORES], "readwrite");
      const assignments = tx.objectStore("bundle_assignments");
      const versions = tx.objectStore("bundle_template_versions");
      const targets = tx.objectStore("bundle_targets");
      const kits = tx.objectStore("bundle_kits");
      const supplements = tx.objectStore("bundle_supplements");
      const facilities = tx.objectStore("bundle_facilities");
      const walkups = tx.objectStore("bundle_walkup_templates");
      const requirements = tx.objectStore("bundle_requirements");

      // Kits are tester-personal (the caller's RLS-visible kits), not
      // org-partitioned, so every pull wholesale-replaces them regardless of
      // scope — zero merge logic, same posture as the other bundle arms.
      await requestToPromise(kits.clear());

      // Pending-preserve prune: a locally-created walk-up assignment queued
      // (or errored) in outbox_assignments must survive the wholesale
      // replace, because the server cannot echo it back until it has been
      // pushed.
      const preserved = await pendingWalkupAssignmentIds(tx);

      if (scope.kind === "field") {
        // The field-scope guard is belt-and-suspenders only — walk-up is
        // org-arm-only and a field-mode user has zero org memberships, so
        // the preserved set is always empty here and the delete is a full
        // wipe. The full wipe of versions/targets below still drops the
        // satellites, which is fine precisely because the preserved-row case
        // is unreachable on this arm.
        await deleteRowsWhere<AssignmentRow>(assignments, (row) => !preserved.has(row.id));
        await requestToPromise(versions.clear());
        await requestToPromise(targets.clear());
        await requestToPromise(facilities.clear());
        await requestToPromise(walkups.clear());
        await requestToPromise(requirements.clear());
        // Supplements are company-private and ride the field bundle only;
        // org members never execute them. Wholesale-replace on every field
        // pull — as are the three change-request stores, field-only too.
        await requestToPromise(supplements.clear());
        await requestToPromise(tx.objectStore("bundle_category_templates").clear());
        await requestToPromise(tx.objectStore("bundle_change_request_sets").clear());
        await requestToPromise(tx.objectStore("bundle_catalog_versions").clear());
      } else {
        const orgId = scope.orgId;
        await deleteRowsWhere<AssignmentRow>(
          assignments,
          (row) => row.org_id === orgId && !preserved.has(row.id),
        );
        await deleteRowsWhere<FacilityRow>(facilities, (row) => row.org_id === orgId);
        await deleteRowsWhere<WalkupTemplateRow>(walkups, (row) => row.org_id === orgId);
        await deleteRowsWhere<RequirementsRow>(requirements, (row) => row.org_id === orgId);
        // The keep-set is computed from the assignments REMAINING after the
        // delete above and MUST stay after it: that is what lets a preserved
        // local row automatically retain its version + target satellites.
        const remaining = await getAllRows<AssignmentRow>(assignments);
        const keepVersionIds = new Set(remaining.map((r) => r.template_version_id));
        const keepTargetKeys = new Set(
          remaining.map((r) => targetKey(r.facility_id, r.device_installation_id)),
        );
        await deleteRowsWhere<TemplateVersionRow>(versions, (row) => !keepVersionIds.has(row.id));
        await deleteRowsWhere<TargetRow>(targets, (row) => !keepTargetKeys.has(row.target_key));
      }

      for (const a of bundle.assignments) {
        assignments.put(assignmentRow(a));
      }
      for (const v of bundle.template_versions) {
        versions.put({
          id: v.id,
          template_id: v.template_id,
          version: v.version,
          title: v.title,
          target_kind: v.target_kind,
          device_category: v.device_category ?? null,
          review_required: v.review_required,
          // The received definition serialized exactly once at store time —
          // the renderer parses THIS string.
          definition_json: JSON.stringify(v.definition),
        } satisfies TemplateVersionRow);
      }
      for (const t of bundle.targets) {
        targets.put({
          target_key: t.target_key,
          facility_id: t.facility_id,
          device_installation_id: t.device_installation_id ?? null,
          payload_json: JSON.stringify(t),
        } satisfies TargetRow);
      }
      for (const k of bundle.kits) {
        kits.put({
          id: k.id,
          owner_kind: k.owner_kind,
          owner_label: deriveKitOwnerLabel(k),
          manufacturer: k.manufacturer,
          model: k.model ?? null,
          serial_number: k.serial_number,
          status: k.status,
          calibrated_at: k.latest_calibration?.calibrated_at ?? null,
          calibration_expires_at: k.latest_calibration?.calibration_expires_at ?? null,
        } satisfies KitRow);
      }
      // Supplements ride the field bundle only. The field response omits the
      // array for a tester with no company, so guard the nullish; the
      // definition is serialized once at store time, mirroring the
      // template-version arm.
      if (scope.kind === "field") {
        for (const s of bundle.supplement_templates ?? []) {
          supplements.put({
            supplement_key: supplementKey(s.company_id, s.template_version_id),
            company_id: s.company_id,
            company_name: s.company_name,
            device_category: s.device_category ?? null,
            template_id: s.template_id,
            template_version_id: s.template_version_id,
            title: s.title,
            definition_json: JSON.stringify(s.definition),
          } satisfies SupplementRow);
        }
        const templates = tx.objectStore("bundle_category_templates");
        for (const t of bundle.category_templates) {
          templates.put({
            template_key: categoryTemplateKey(t.org_id, t.device_category),
            payload_json: JSON.stringify(t),
          } satisfies CategoryTemplateRow);
        }
        const sets = tx.objectStore("bundle_change_request_sets");
        bundle.change_request_sets.forEach((set, position) => {
          sets.put({
            id: set.id,
            position,
            payload_json: JSON.stringify(set),
          } satisfies ChangeRequestSetBundleRow);
        });
        const catalogVersions = tx.objectStore("bundle_catalog_versions");
        for (const [orgId, token] of Object.entries(bundle.catalog_versions)) {
          catalogVersions.put({ org_id: orgId, token } satisfies CatalogVersionRow);
        }
      }
      for (const f of bundle.facilities) {
        facilities.put(facilityRow(f));
      }
      // One readiness block per org the tester holds a non-revoked grant with.
      // The org home carries none (a utility staffer holds no grants), so the
      // org arm deletes that org's row above and writes nothing back. Each row
      // takes the utility block naming its own org; the field home's utility
      // blocks cover only active, company-cleared grants, and the org home's
      // bundle carries none.
      const utilities = new Map((bundle.utilities ?? []).map((u) => [u.org_id, u]));
      for (const u of bundle.requirements) {
        requirements.put(requirementsRow(u, utilities.get(u.org_id)));
      }
      // Walk-up templates are org-home-only (the field wire contract sends an
      // empty array), so bind org_id from the org scope; the field arm never
      // iterates this loop.
      if (scope.kind === "org") {
        for (const w of bundle.walkup_templates) {
          walkups.put({
            template_id: w.template_id,
            org_id: scope.orgId,
            template_version_id: w.template_version_id,
            title: w.title,
            target_kind: w.target_kind,
          } satisfies WalkupTemplateRow);
        }
      }
      // Persist the grant-scope truncation flag, the pre-cap total and the
      // held count per scope so the facility banners read them via
      // getSyncMeta. The scope suffix mirrors the device store's sync-meta
      // key: "field" | `org_${orgId}`.
      const scopeKey = scope.kind === "field" ? "field" : `org_${scope.orgId}`;
      const meta = tx.objectStore("sync_meta");
      meta.put({
        key: `facilities_truncated_${scopeKey}`,
        value: String(bundle.facilities_truncated),
      } satisfies SyncMetaRow);
      meta.put({
        key: `facility_total_${scopeKey}`,
        value: String(bundle.facility_total),
      } satisfies SyncMetaRow);
      meta.put({
        key: `facilities_held_${scopeKey}`,
        value: String(bundle.facilities.length),
      } satisfies SyncMetaRow);
      await txDone(tx);
    },

    async listBundleAssignments() {
      const rows = await getAllRows<AssignmentRow>(readStore("bundle_assignments"));
      rows.sort((x, y) => compareStrings(x.due_date, y.due_date) || compareStrings(x.id, y.id));
      return rows.map(toStoredAssignment);
    },

    async getBundleAssignment(id) {
      const row = await getRow<AssignmentRow>(readStore("bundle_assignments"), id);
      return row === null ? null : toStoredAssignment(row);
    },

    async setLocalStatus(assignmentId, localStatus) {
      const tx = db.transaction("bundle_assignments", "readwrite");
      const store = tx.objectStore("bundle_assignments");
      const row = await getRow<AssignmentRow>(store, assignmentId);
      // Mirrors the device store's UPDATE: an unknown id matches zero rows.
      if (row !== null) {
        row.local_status = localStatus;
        store.put(row);
      }
      await txDone(tx);
    },

    async getTemplateVersion(id) {
      const row = await getRow<TemplateVersionRow>(readStore("bundle_template_versions"), id);
      if (row === null) return null;
      return {
        id: row.id,
        templateId: row.template_id,
        version: row.version,
        title: row.title,
        targetKind: row.target_kind,
        deviceCategory: row.device_category,
        reviewRequired: row.review_required,
        definitionJson: row.definition_json,
      };
    },

    async getTarget(facilityId, deviceInstallationId) {
      const row = await getRow<TargetRow>(
        readStore("bundle_targets"),
        targetKey(facilityId, deviceInstallationId),
      );
      if (row === null) return null;
      // payload_json was written as JSON.stringify of a wire BundleTarget.
      return JSON.parse(row.payload_json) as BundleTarget;
    },

    async listBundleKits() {
      const rows = await getAllRows<KitRow>(readStore("bundle_kits"));
      rows.sort(
        (x, y) =>
          compareStrings(x.owner_label, y.owner_label) ||
          compareStrings(x.serial_number, y.serial_number),
      );
      return rows.map((r) => ({
        id: r.id,
        ownerKind: r.owner_kind,
        ownerLabel: r.owner_label,
        manufacturer: r.manufacturer,
        model: r.model,
        serialNumber: r.serial_number,
        status: r.status,
        calibratedAt: r.calibrated_at,
        calibrationExpiresAt: r.calibration_expires_at,
      }));
    },

    async listSupplements() {
      const rows = await getAllRows<SupplementRow>(readStore("bundle_supplements"));
      rows.sort(
        (x, y) =>
          compareStrings(x.company_name, y.company_name) || compareStrings(x.title, y.title),
      );
      return rows.map(toStoredSupplement);
    },

    // Resolve the supplement a tester executes for a given company + device
    // category. The category-exact row wins; a NULL-category row is the
    // company-wide wildcard fallback; absent both, there is no supplement.
    // The one-published-per-(company, category) publish rule makes this
    // deterministic.
    async supplementForCompany(companyId, deviceCategory) {
      const rows = await getAllRows<SupplementRow>(
        readStore("bundle_supplements").index("company_id"),
        companyId,
      );
      const stored = rows.map(toStoredSupplement);
      const exact =
        deviceCategory !== null
          ? (stored.find((s) => s.deviceCategory === deviceCategory) ?? null)
          : null;
      if (exact) return exact;
      return stored.find((s) => s.deviceCategory === null) ?? null;
    },

    async getBundleFacility(facilityId) {
      const row = await getRow<FacilityRow>(readStore("bundle_facilities"), facilityId);
      return row === null ? null : toStoredFacility(row);
    },

    // The device store binds likePattern(q) — the trimmed term with the three
    // LIKE metacharacters escaped so they match literally — into
    // `name LIKE $q OR address_line1 LIKE $q OR city LIKE $q` with a
    // name-ascending LIMIT. String.includes has no metacharacters, so the
    // verbatim trimmed term is the same predicate; the ASCII-only fold is
    // asciiLowerCase's contract.
    async searchBundleFacilities(q, limit = 20) {
      const rows = await getAllRows<FacilityRow>(readStore("bundle_facilities"));
      const term = asciiLowerCase(q.trim());
      const matches = (column: string | null): boolean =>
        column !== null && asciiLowerCase(column).includes(term);
      const hits = rows.filter(
        (r) => matches(r.name) || matches(r.address_line1) || matches(r.city),
      );
      hits.sort((x, y) => compareStrings(x.name, y.name));
      return hits.slice(0, limit).map(toStoredFacility);
    },

    async listWalkupTemplates(orgId) {
      const rows = await getAllRows<WalkupTemplateRow>(
        readStore("bundle_walkup_templates").index("org_id"),
        orgId,
      );
      rows.sort((x, y) => compareStrings(x.title, y.title));
      return rows.map((r) => ({
        templateId: r.template_id,
        orgId: r.org_id,
        templateVersionId: r.template_version_id,
        title: r.title,
        targetKind: r.target_kind,
      }));
    },

    async getUtilityRequirements(orgId) {
      const row = await getRow<RequirementsRow>(readStore("bundle_requirements"), orgId);
      return row === null ? null : toStoredUtilityRequirements(row);
    },

    async getCategoryTemplate(orgId, deviceCategory) {
      const row = await getRow<CategoryTemplateRow>(
        readStore("bundle_category_templates"),
        categoryTemplateKey(orgId, deviceCategory),
      );
      // payload_json was written as JSON.stringify of the wire template.
      return row === null ? null : (JSON.parse(row.payload_json) as ChangeRequestCategoryTemplate);
    },

    async listBundleChangeRequestSets() {
      const rows = await getAllRows<ChangeRequestSetBundleRow>(
        readStore("bundle_change_request_sets"),
      );
      rows.sort((x, y) => x.position - y.position);
      // payload_json was written as JSON.stringify of the wire set.
      return rows.map((r) => JSON.parse(r.payload_json) as ChangeRequestSet);
    },

    async getCatalogVersions() {
      const rows = await getAllRows<CatalogVersionRow>(readStore("bundle_catalog_versions"));
      return Object.fromEntries(rows.map((r) => [r.org_id, r.token]));
    },

    // Insert a walk-up assignment created locally on the device plus its
    // synthesized target row in ONE transaction. The assignment mirrors the
    // replaceBundle assignments loop (local_status null, payload_json = the
    // wire row); the target row synthesizes the wire target's required members
    // (target_key / facility_id / facility_name from the facility, and a
    // premise target's empty device_read_context) so readers and the renderer
    // see a complete premise target before the outbox push echoes the
    // server-created assignment back on the next pull.
    async insertLocalWalkupAssignment(input) {
      const { assignment: a, facility } = input;
      const key = targetKey(facility.facilityId, null);
      const tx = db.transaction(["bundle_assignments", "bundle_targets"], "readwrite");
      tx.objectStore("bundle_assignments").put(assignmentRow(a));
      const targetPayload = {
        target_key: key,
        facility_id: facility.facilityId,
        facility_name: facility.name,
        address_line1: facility.addressLine1,
        address_line2: facility.addressLine2,
        city: facility.city,
        state_code: facility.stateCode,
        zip_code: facility.zipCode,
        device_read_context: {},
      };
      tx.objectStore("bundle_targets").put({
        target_key: key,
        facility_id: facility.facilityId,
        device_installation_id: null,
        payload_json: JSON.stringify(targetPayload),
      } satisfies TargetRow);
      await txDone(tx);
    },

    async getSyncMeta(key) {
      const row = await getRow<SyncMetaRow>(readStore("sync_meta"), key);
      return row === null ? null : row.value;
    },

    async setSyncMeta(key, value) {
      const tx = db.transaction("sync_meta", "readwrite");
      tx.objectStore("sync_meta").put({ key, value } satisfies SyncMetaRow);
      await txDone(tx);
    },
  };
}
